/* Global window */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import _debounce from 'lodash/debounce';
import _throttle from 'lodash/throttle';
import _get from 'lodash/get';
import styled from 'styled-components';
import { breakPoints } from '../config/';
import { horizontalPadding } from './DisplayComponents';
import { Swipeable } from 'react-swipeable';
import ArrowLeftIcon from '../assets/carousel-arrow-left.svg';
import ArrowRightIcon from '../assets/carousel-arrow-right.svg';
import furhterLinkIndicator from '../assets/further-link-indicator.svg';


const { maxMobile } = breakPoints;

const swipeConfig = {
  delta: 10,
  preventDefaultTouchmoveEvent: false,
  trackTouch: true,
  trackMouse: false,
  rotationAngle: 0,
}

const Container = styled.div`
  position: relative;
  width: 100%;
  overflow-x: hidden;
  padding-bottom: 42px;
  @media(max-width: ${breakPoints.maxMobile}px) {
    padding-bottom: 32px;
  }
`;

const SlidesContainer = styled.div`
  display: flex;
  transform: translateX(-100%);
`;

const SlideContainer = styled.div`
  flex: 0 0 100%;
  > div {
    display: flex;
    max-width: 1000px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
    padding: 0 ${horizontalPadding};
    flex-direction: ${props => props.textFirst ? 'row-reverse' : 'row'};
    @media(max-width: ${breakPoints.maxMobile}px) {
      flex-direction: column;
      padding: 0 0;
    }
    > div {
      width: 50%;
      @media(max-width: ${breakPoints.maxMobile}px) {
        width: 100%;
      }
    }
  }
`;

const ImageWrapper = styled.div`
  width: 488px;
  height: 488px;
  background-color: #15116F;
  @media(max-width: ${maxMobile}px) {
    width: 100%;
    height: 375px;
  };
  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ArrowIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  box-sizing: border-box;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 1;
  > img {
    height: 100%;
    width: 100%;
    object-fit: contain;
    display: block;
  }
  @media(max-width: ${breakPoints.maxMobile}px) {
    display: none;
  }
`;

const ArrowLeft = styled(ArrowIconWrapper)`
  left: 20px;
`;

const ArrowRight = styled(ArrowIconWrapper)`
  right: 20px;
`;


const DotsContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
`;

const DotWrapper = styled.div`
  margin-right: 16px;
  padding: 3px;
  cursor: pointer;
`;

const Dot = styled.div`
  border-radius: 50%;
  background-color: ${props => props.highlighted ? props.theme.blue : '#C4C4C4'};
  width: ${props => props.highlighted ? '12px' : '10px'};
  height: ${props => props.highlighted ? '12px' : '10px'};
  opacity: ${props => props.highlighted ? '1' : '0.6'};
`;

const ContentWrapper = styled.div`
  width: 80%;
  margin: ${props => props.textFirst ? '30px auto 0 0' : '30px 0 0 auto'};
  box-sizing: border-box;
  @media(max-width: ${breakPoints.maxMobile}px) {
    width: 100%;
    margin: 30px 0 0 0;
    padding: 0 ${horizontalPadding};
  }
`;

const Title = styled.h3`
  margin-bottom: 10px;
`;

const Text = styled.p``;

const ExternalLinkWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  > div {
    color: ${props => props.theme.blue};
    font-weight: bold;
  }
`;

const FLA = styled.a`
  &:hover {
    color: ${props => props.theme.blue};
    transition: 0.3s;
    border-color: ${props => props.theme.blue} !important;
  }
  &:link {
    color: ${props => props.theme.blue};
    text-decoration: none;
    border-bottom: 1px solid rgb(216, 216, 216);
  }
  &:active {
    color: ${props => props.theme.blue};
    text-decoration: none;
    border-bottom: 1px solid rgb(216, 216, 216);
  }
  &:visited {
    color: ${props => props.theme.blue};
    text-decoration: none;
    border-bottom: 1px solid rgb(216, 216, 216);
  }
`;

const _ = {
  get: _get,
  debounce: _debounce,
  throttle: _throttle,
};

const transitionTime = '500';

class Carousel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      index: 1,
    };
    this.intervalId = null;
    this.slidesContainer = null;
    this.next = this._next.bind(this);
    this.prev = this._prev.bind(this);
    this.setIndex = this._setIndex.bind(this);
    this.jump = this._jump.bind(this);
    this.setTransform = this._setTransform.bind(this);
    this.onSwipeHandler = this._onSwipeHandler.bind(this);
    this.isSwiping = false;
    this.extendedData = (() => {
      const { data } = props;
      const newSrcs = data.slice(0, data.length);
      newSrcs.push(data[0]);
      newSrcs.unshift(data[data.length - 1]);
      return newSrcs;
    })();
  }

  componentDidMount() {
    window.addEventListener('resize', _.debounce(() => {
      this.setTransform(this.state.index);
    }, 500, { trailing: true }));
    this.slidesContainer.addEventListener('transitionend', () => {
      const { index } = this.state;
      if (index === 0) {
        this._setTransform(this.extendedData.length - 2, true);
        return;
      }
      if (index === this.extendedData.length - 1) {
        this._setTransform(1, true);
      }
    });
    const { ifInitiateAutoCrousel } = this.props;
    if (ifInitiateAutoCrousel) {
      this._initiateAutoCarousel();
    }
  }

  componentWillUnmount() {
    clearInterval(this.intervalId)
  }

  _initiateAutoCarousel() {
    this.intervalId = setInterval(this.next, 5000);
  }

  _setIndex(index) {
    this.setState({
      index,
    });
  }

  _jump(index) {
    if (this.slidesContainer) {
      this._setTransform(index);
    }
  }

  _next() {
    const { index } = this.state;
    if (this.slidesContainer && this.extendedData) {
      if (index < this.extendedData.length - 1) {
        this._setTransform(index + 1);
      }
    }
  }

  _prev() {
    const { index } = this.state;
    if (this.slidesContainer && this.extendedData) {
      if (index > 0) {
        this._setTransform(index - 1);
      }
    }
  }

  _setTransform(index, hasNoSmooth) {
    this.setState({
      index,
    }, () => {
      const elem = this.slidesContainer;
      const distance = _.get(elem, 'offsetWidth', 0);
      if (hasNoSmooth) {
        // Reset to first and last image instantly.
        elem.style.transition = 'null';
      } else {
        elem.style.transition = `transform ${transitionTime}ms ease-in-out`;
      }
      elem.style.transform = `translate3d(${(-(this.state.index)) * distance}px, 0, 0)`;
    });
  }

  _onSwipeHandler(eventData) {
    const { deltaX } = eventData;
    if (this.isSwiping) {
      return;
    }
    this.isSwiping = true;
    if (deltaX > 13) {
      this._next();
    }
    if (deltaX < -13) {
      this._prev();
    }
  }


  _renderButton(type) {
    const buttonTypes = {
      pre: 'previous',
      nex: 'next',
    }
    if (type === buttonTypes.pre) {
      return (
        <ArrowLeft
          onClick={this.prev}
        >
          <img
            src={ArrowLeftIcon}
            alt={'carousel button left'}
          />
        </ArrowLeft>
      );
    }
    if (type === buttonTypes.nex) {
      return (
        <ArrowRight
          onClick={this.next}
        >
          <img
            src={ArrowRightIcon}
            alt="carousel button right"
          />
        </ArrowRight>
      );
    }
    return null;
  }

  _rednerSlide() {
    const { textFirst } = this.props;
    return this.extendedData.map((entity, localIndex) => {
      const { src, title, text, external } = entity;
      let externalLink = null;
      if (external) {
        externalLink = external.map(({
          title, content, href
        }, index) => {
          return (
            <ExternalLinkWrapper
              key={`extLink_takeaway_${title}_${index}`}
            >
              {title && <div style={{ whiteSpace: 'nowrap' }}>{title}</div>}
              <img
                width="24"
                height="24"
                src={furhterLinkIndicator} alt="link icon"
                style={{
                  margin: '0 10px 0 5px',
                }}
              />
              <FLA
                href={href}
                target="_blank"
                rel="noopener noreferer"
              >
                <div>{content}</div>
              </FLA>
            </ExternalLinkWrapper>
          );
        })
      }
      return (
        <SlideContainer
          textFirst={textFirst}
          key={`Slide_BackgroundImage_Src_${src}_${localIndex}`}
        >
          <div>
            <ImageWrapper>
              <img
                className="lazy"
                data-src={src}
                alt={`img_${title}`}
              />
            </ImageWrapper>
            <div>
              <ContentWrapper
                textFirst={textFirst}
              >
                {title && <Title>{title}</Title>}
                <Text
                  dangerouslySetInnerHTML={{__html: `${text}`}}
                />
                {externalLink}
              </ContentWrapper>
            </div>
          </div>
        </SlideContainer>
      );
    });
  }

  _renderDots() {
    const { index } = this.state;
    return this.extendedData.map((src, localIndex) => {
      if (localIndex === 0 || localIndex === this.extendedData.length - 1) {
        return null;
      }
      const currentIndex = (() => {
        if (index === 0) {
          return this.extendedData.length - 2;
        }
        if (index === this.extendedData.length - 1) {
          return 1;
        }
        return index;
      })();
      return (
        <DotWrapper
          key={`dot_to_background_image_${src}_${localIndex}`}
          onClick={() => { this.jump(localIndex); }}
        >
          <Dot
            highlighted={localIndex === currentIndex}
          />
        </DotWrapper>
      );
    });
  }

  render() {
    return (
      <Container>
        {this._renderButton('previous')}
        {this._renderButton('next')}
        <Swipeable
          onSwiping={this.onSwipeHandler}
          onSwiped={() => {
            this.isSwiping = false;
          }}
          {...swipeConfig}
        >
          <SlidesContainer
            ref={(node) => { this.slidesContainer = node; }}
          >
            {this._rednerSlide()}
          </SlidesContainer>
        </Swipeable>
        <DotsContainer>
          {this._renderDots()}
        </DotsContainer>
      </Container>
    );
  }
}

Carousel.defaultProps = {
  textFirst: false,
};

Carousel.propTypes = {
  data: PropTypes.array,
  textFirst: PropTypes.bool,
};

export default Carousel;
